/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import moment, { Moment } from 'moment'

import i18n from 'live/services/i18n'
import LiveSelect from 'live/components/forms/ui/select'
import globalNotification from 'live/services/global-notification'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'

import { Period, Template } from '../../../types'
import { TemplateFormProps } from '../TemplateFormRenderer'
import { FormRenderer } from '../../../formBuilder/components/FormRenderer'
import { fetchActiveTemplates, getTemplate } from '../../../services/template'
import { AnnotationEmptyData, FormContainer, StyledSection } from '../../styles'

interface NewAnnotationProps extends TemplateFormProps {
    submitFormData: (data: Record<string, unknown>, template: Template, period: Period) => void
}

const NewAnnotation: React.FC<NewAnnotationProps> = (props: NewAnnotationProps) => {
    const [template, setTemplate] = useState<Template>(null)
    const [templateOptions, setTemplateOptions] = useState<SelectOption[]>([])
    const [loadingTemplateOptions, setLoadingTemplateOptions] = useState<boolean>(true)
    const [period, setPeriod] = useState<Period>({ begin: props.event.begin, end: props.event.end })
    const [annotationData, setAnnotationData] = useState<Record<string, unknown>>()
    const [selectedTemplate, setSelectedTemplate] = useState<SelectOption>({
        value: '',
        label: ''
    })

    const handleOnChangePeriod = (value: Moment, key: 'begin' | 'end'): void => {
        setPeriod(prevState => ({ ...prevState, [key]: moment(value).valueOf() }))
    }

    useEffect(() => {
        const fetchTemplate = async (): Promise<void> => {
            try {
                const template = await getTemplate(selectedTemplate.value)

                setTemplate(template)
            } catch (error) {
                globalNotification.error(i18n(error.message), 5)
            }
        }

        fetchTemplate()
    }, [selectedTemplate])

    useEffect(() => {
        const fetchTemplatesList = async (): Promise<void> => {
            try {
                const templatesList = await fetchActiveTemplates(props.dashboardId)

                setTemplateOptions(
                    templatesList.map(template => {
                        return {
                            value: template.id,
                            label: template.alias
                        } as SelectOption
                    })
                )
            } catch (error) {
                globalNotification.error(i18n(error.message), 5)
            }

            setLoadingTemplateOptions(false)
        }

        fetchTemplatesList()
    }, [])

    return (
        <>
            {!loadingTemplateOptions && templateOptions.length > 0 ? (
                <>
                    <FormContainer>
                        <div className="template-select">
                            <LiveSelect
                                clearable={false}
                                value={selectedTemplate}
                                options={templateOptions}
                                label={i18n('Templates')}
                                disabled={loadingTemplateOptions}
                                placeholder={loadingTemplateOptions ? i18n('Loading...') : i18n('Select a template')}
                                help={`${i18n("Templates must match the dashboard's perspective")}.`}
                                onChange={(_: string, option: React.SetStateAction<SelectOption | undefined>) => {
                                    setSelectedTemplate(option)
                                }}
                            />
                        </div>
                        {template && template.schema && period.begin && (
                            <FormRenderer
                                mode="edit"
                                period={period}
                                data={annotationData}
                                schema={JSON.parse(template.schema) as FormSchema}
                                onChange={data => setAnnotationData(data)}
                                onChangePeriod={handleOnChangePeriod}
                            />
                        )}
                    </FormContainer>
                    {props.showActions && (
                        <StyledSection>
                            <SubmitOrCancel
                                btnsClassName="btn-lg btn-bold"
                                disabled={!template}
                                saving={false}
                                saved={false}
                                onSubmit={() => props.submitFormData(annotationData, template, period)}
                                onCancel={props.onHide}
                            />
                        </StyledSection>
                    )}
                </>
            ) : (
                <AnnotationEmptyData
                    title={i18n('No templates found')}
                    subtitle={`${i18n("Template perspective must match the dashboard's perspective")}.`}
                    icon="far fa-empty-set"
                />
            )}
        </>
    )
}

export default NewAnnotation
